import React, { useState, useEffect, useCallback } from "react";
import { Box, Snackbar, Alert } from "@mui/material";
import { makeRequest } from "../../../../assets/functions_helper";
import { useNavigate } from "react-router-dom";
import ListData from "../../../../components/listData";
import CreateProductDialog from "./create_product";

import EditProductDialog from "./edit_product";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { useSubscriptionUsage } from "../../../../context/SubscriptionUsageContext";

const columnsConfig = [
  {
    header: "Imagen",
    dataKey: "image_url",
    type: "image",
  },
  {
    header: "Categoría",
    dataKey: "category",
    type: "text"
  },
  {
    header: "Precio",
    dataKey: "formattedPrice",
    type: "text"
  }
];

function ListProducts({ restaurantId, initialFilter }) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState(false);
  const [openDialogEdit, setOpenDialogEdit] = useState(false);
  const [productId, setProductId] = useState("");
  const [initialData, setInitialData] = useState({});

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info"
  });

  const { usageInfo, fetchUsage } = useSubscriptionUsage();
  const navigate = useNavigate();

  useEffect(() => {
    if (initialFilter) {
      setSearchTerm(initialFilter);
    }
  }, [initialFilter]);

  const fetchProducts = useCallback(() => {
    makeRequest(
      "GET",
      `menu_manager/list_restaurant_products?restaurant_id=${restaurantId}&page=${page}&size=200`,
      {},
      "application/json",
      localStorage.getItem("token"),
      localStorage.getItem("tokenType"),
      null,
      null,
      () => navigate("/login")
    )
      .then((response) => {
        const rawProducts = response.products || [];
        const updatedProducts = rawProducts.map((product) => ({
          id: product.id,
          title: product.name,
          image_url: product.image_url,
          category: product.category,
          formattedPrice: `$ ${Number(product.price).toLocaleString()}`,
          originalData: product,
          is_active: product.is_active ?? false,
          public_id: product.id
        }));

        setProducts(updatedProducts);

        const uniqueCats = [
          ...new Map(
            rawProducts
              .filter(p => p.category)
              .map((p) => [p.category, { id: p.category, title: p.category }])
          ).values(),
        ];

        setCategories([{ id: "", title: "Todas las categorías" }, ...uniqueCats]);

        setTotal(response.total);
        setPages(response.pages);
        setIsLoaded(true);
      })
      .catch((error) => {
        console.log(error);
      });
  }, [restaurantId, page, navigate]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  useEffect(() => {
    let result = [...products];
    if (selectedCategory && selectedCategory !== "") {
      result = result.filter((p) => p.category === selectedCategory);
    }
    if (searchTerm.trim() !== "") {
      const lowerTerm = searchTerm.toLowerCase();
      result = result.filter((p) => p.title.toLowerCase().includes(lowerTerm));
    }
    setFilteredProducts(result);
  }, [products, selectedCategory, searchTerm]);

  const handleFilterChange = (e) => {
    const val = e.target ? e.target.value : e;
    setSelectedCategory(val);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleClose = () => {
    setOpen(false);
    refreshStates();
  };

  const refreshStates = () => {
    // Si necesitas reiniciar estados extra en list_products, hazlo aquí.
  };

  const handleOpenEdit = (id, item) => {
    setProductId(id);
    setInitialData(item.originalData);
    setOpenDialogEdit(true);
  };

  const activeOrDesableProducts = (e, item) => {
    const checked = e.target.checked;
    const updatedList = products.map((product) =>
      product.id === item.id ? { ...product, is_active: checked } : product
    );
    setProducts(updatedList);

    const formData = new FormData();
    formData.append("updated_product", JSON.stringify({ is_active: checked }));

    makeRequest(
      "PUT",
      `menu_manager/update_product/${item.id}`,
      formData,
      null, // Elimina el Content-Type para permitir a axios y al navegador asignar el boundary
      localStorage.getItem("token"),
      localStorage.getItem("tokenType"),
      null,
      null,
      () => navigate("/login")
    ).catch(() => {
      setProducts(products);
      setSnackbar({
        open: true,
        message: "Error al actualizar estado",
        severity: "error"
      });
    });
  };

  const handleEnhanceImage = (id) => {
    setSnackbar({
      open: true,
      message: "✨ Mejorando imagen con IA... Por favor espera.",
      severity: "info"
    });

    makeRequest(
      "PUT",
      `menu_manager/enhance_image?product_id=${id}`,
      {},
      "application/json",
      localStorage.getItem("token"),
      localStorage.getItem("tokenType"),
      null,
      null,
      () => navigate("/login")
    )
      .then(() => {
        setSnackbar({
          open: true,
          message: "✅ Imagen mejorada exitosamente.",
          severity: "success"
        });
        fetchProducts();
        fetchUsage(restaurantId);
      })
      .catch(() => {
        setSnackbar({
          open: true,
          message: "❌ Error al mejorar la imagen.",
          severity: "error"
        });
      });
  };

  const extraActions = [
    {
      icon: <AutoAwesomeIcon fontSize="small" />,
      onClick: (item) => handleEnhanceImage(item.id),
      show: (item) => !item.originalData?.ia_enhanced,
      disabled: (item) => usageInfo?.hasSubscription && usageInfo?.ai_prompts?.remaining <= 0,
      tooltip: "Mejorar imagen con IA",
      disabledTooltip: "Límite de mejoras con IA alcanzado. Mejora tu plan.",
    },
  ];

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  return (
    <>
      {isLoaded ? (
        <>
          <ListData
            title="Mis Productos"
            data={filteredProducts}
            pages={pages}
            total={total}
            search={true}
            searchValue={searchTerm}
            searchFunction={handleSearchChange}
            searchPlaceholder="Buscar por nombre..."
            filterSelect={true}
            valuesFilter={categories}
            valueFilter={selectedCategory}
            filterSelectFunction={handleFilterChange}
            create={true}
            createMessage="Nuevo Producto"
            createFunction={() => setOpen(true)}
            createLimitMessage={
              usageInfo?.hasSubscription && usageInfo?.products?.remaining <= 0
                ? "Límite de productos alcanzado"
                : ""
            }
            edit={true}
            editFunction={(id, title, public_id, item) => handleOpenEdit(id, item)}
            pagination={true}
            setPage={setPage}
            page={page}
            nameColumn1="Producto"
            nameColumn2={null}
            nameColumn3="Acciones"
            dynamicColumns={columnsConfig}
            showSwitch={true}
            onSwitchChange={activeOrDesableProducts}
            extraActions={extraActions}
          />

          <CreateProductDialog
            open={open}
            handleClose={handleClose}
            restaurantId={restaurantId}
            fetchProducts={fetchProducts}
          />

          <EditProductDialog
            open={openDialogEdit}
            handleClose={() => {
              setOpenDialogEdit(!openDialogEdit);
              refreshStates();
            }}
            productId={productId}
            restaurantId={restaurantId}
            initialData={initialData}
            fetchProducts={fetchProducts}
          />

          <Snackbar
            open={snackbar.open}
            autoHideDuration={4000}
            onClose={handleSnackbarClose}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <Alert
              onClose={handleSnackbarClose}
              severity={snackbar.severity}
              variant="filled"
              sx={{ width: '100%', fontWeight: 500, boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}
            >
              {snackbar.message}
            </Alert>
          </Snackbar>
        </>
      ) : (
        <Box sx={{ height: "100vh" }} />
      )}
    </>
  );
}

export default ListProducts;